/**
 * Google's pass-through links in SERP results (TECHNICAL_DEBT §0cs, docs/features/SERP_GOTO_LINKS.md).
 *
 * Google serves `google.com/goto?url=…` links in place of the ranking page's URL. DataForSEO follows
 * them for us, but since 2026-11 Google caps how many it will resolve per results page, so some rows
 * still come back as a `goto` link. The link cannot be decoded, only followed, and following it is
 * scraping Google, so such a row is *unresolved*: we know the site (from the breadcrumb), never the
 * page. It must never be crawled, compared against, or matched to a page by URL.
 *
 * Byte-identical in `shared/src/review/` and `backend/src/utils/`, because `compare-target.ts` exists
 * in both; `backend/src/utils/__tests__/serp-unresolved-url.test.ts` fails when they differ.
 */

const GOOGLE_PASS_THROUGH = /^https?:\/\/(?:www\.)?google\.[a-z.]+\/(?:goto|url)(?:[?/#]|$)/i;

/** True when a SERP row's URL is a Google pass-through link rather than the ranking page. */
export function isUnresolvedSerpUrl(url: string | null | undefined): boolean {
  return GOOGLE_PASS_THROUGH.test(String(url || '').trim());
}

/**
 * The site a SERP row's breadcrumb names (`https://www.investopedia.com › ... › Warren Buffett`),
 * www-less and lowercased; null when it does not start with a host. The path after the host is
 * truncated by Google (`› ... ›`), so the breadcrumb gives the site, never the page.
 */
export function serpBreadcrumbDomain(breadcrumb: string | null | undefined): string | null {
  const head = String(breadcrumb || '').split('›')[0].trim();
  if (!head) return null;
  const host = head
    .replace(/^https?:\/\//i, '')
    .split(/[/\s?#]/)[0]
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\.$/, '');
  return /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/.test(host) ? host : null;
}

/** How many rows are Google pass-through links rather than pages. */
export function countUnresolvedSerpRows(rows: ReadonlyArray<{ url?: string | null }>): number {
  return rows.filter((row) => isUnresolvedSerpUrl(row.url)).length;
}
